import {
  CreditCard,
  IdCard,
  LayoutGrid,
  Settings,
  Link2,
  Ship,
  Smartphone,
  Truck,
  Users,
  Wallet,
  Warehouse,
  type LucideIcon,
} from 'lucide-react'
import type menuLabels from '@/i18n/navigation.json'
import type { MenuItem } from '@/types/api'

/** A label key of navigation.json (labels the app itself owns, e.g. «Панель управления»). */
export type MenuKey = keyof typeof menuLabels

export type MenuNames = { en: string; ru: string; uz: string; cyr: string }

/**
 * A page link. It resolves to the nav item that owns `path`; when no nav item
 * owns it yet (a page another slice is still building) it is shown locked with
 * `icon`. `search` selects a view of a shared page (e.g. `view=accounts`).
 * `permissions` (all) and `anyPermissions` (one of) are the rights that list it.
 */
export type MenuEntry = {
  kind: 'entry'
  id: string
  label: string
  names: MenuNames
  path: string
  search?: string
  permissions: string[]
  anyPermissions: string[]
  icon?: LucideIcon
  /** Ucode nests a few tables under another table menu. */
  children?: MenuEntry[]
}

/** A collapsible folder. `id` is stable: it keys the remembered open state. */
export type MenuFolder = { kind: 'folder'; id: string; label: string; names: MenuNames; children: MenuNode[] }

export type MenuNode = MenuEntry | MenuFolder

export type MenuArea = {
  id: string
  label: string
  names: MenuNames
  icon: LucideIcon
  children: MenuNode[]
  /** Listed in the account (profile) menu instead of the navigation. */
  inAccountMenu?: boolean
}

/** The icons the menu rows name; an unknown name falls back per caller. */
const icons: Record<string, LucideIcon> = { CreditCard, IdCard, LayoutGrid, Link2, Settings, Ship, Smartphone, Truck, Users, Wallet, Warehouse }

const namesOf = (item: MenuItem): MenuNames => ({ en: item.name_en, ru: item.name_ru, uz: item.name_uz, cyr: item.name_cyr })

/**
 * Only folders and pages are menu rows. The windows, tabs and buttons under a
 * page are role settings (/me/context still sends the ticked ones); the menus
 * never list them — every menu reads the tree through toMenuAreas.
 */
const listed = (item: MenuItem) => item.kind === 'folder' || item.kind === 'page'

function toNode(item: MenuItem): MenuNode {
  if (item.kind === 'folder') {
    return { kind: 'folder', id: item.code, label: item.label_key, names: namesOf(item), children: item.children.filter(listed).map(toNode) }
  }
  const children = item.children.filter(listed).map(toNode).filter((child): child is MenuEntry => child.kind === 'entry')
  return {
    kind: 'entry',
    id: item.code,
    label: item.label_key,
    names: namesOf(item),
    path: item.path,
    search: item.search || undefined,
    permissions: item.permission_codes ?? [],
    anyPermissions: item.any_permission_codes ?? [],
    icon: icons[item.icon],
    children: children.length ? children : undefined,
  }
}

/**
 * The menu tree the server keeps (menu_items; /me/context narrows it to the
 * profile, /menu-items is the whole of it) in the shape the menus render.
 */
export function toMenuAreas(items: MenuItem[]): MenuArea[] {
  return items
    .filter((item) => item.kind === 'area')
    .map((item) => ({
      id: item.code,
      label: item.label_key,
      names: namesOf(item),
      icon: icons[item.icon] ?? LayoutGrid,
      children: item.children.filter(listed).map(toNode),
      inAccountMenu: item.in_account_menu || undefined,
    }))
}
